import { cn } from '@bem-react/classname'
import { FC, FormEvent, useCallback, useState } from 'react'
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import * as stripeJs from '@stripe/stripe-js'

import { Memo } from '@/hoc/Memo'

import { Button } from '@/components/Button'

import './PaymentForm.scss'
import { useTranslation } from 'react-i18next'
import useBackendError from '@/hooks/useBackendError'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import {
  getPaymentsThunk,
  newLicenseThunk,
  renewalsLicenseThunk,
} from '@/core/services/Payments/store/payments.slice'
import {
  CustomerModel,
  NewLicenseModel,
  StateIdModel,
  SuccessType,
} from '../../models/LicensesModel'
import { parse } from 'query-string'
import {
  PaymentsRequestModel,
  PaymentsResponseModel,
} from '@/core/services/Payments/models/PaymentsModel'
import { useLocation } from 'react-router-dom'
import { Transition } from 'react-transition-group'
import { roleSelector } from '@/modules/AccountPage/store/accont.selector'
import { RoleUserModel } from '@/core/services/Token/model/TokenModel'
import { PAYMENT_CONFIRM_CHECK_DELAY_MS } from './constants'

const cnPaymentForm = cn('PaymentForm')

interface PaymentFormInterface {
  onReadyCardElement: () => void
  success: (successType: SuccessType) => void
}

export const PaymentForm: FC<PaymentFormInterface> = Memo(
  ({ onReadyCardElement, success }: PaymentFormInterface) => {
    const { t } = useTranslation('translation')
    const { search } = useLocation()
    const stripe = useStripe()
    const elements = useElements()
    const dispatch = useAppDispatch()
    const [setBackendError] = useBackendError()
    const role = useAppSelector(roleSelector)

    const [message, setMessage] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)

    const getPaymentStatus = async (paymentId: string): Promise<void> => {
      const res = await dispatch(getPaymentsThunk(paymentId)).unwrap()

      switch (res?.stateId) {
        case StateIdModel.SUCCESS: {
          setIsLoading(false)

          const s = parse(search)
          const licenseId = s.licenseId || ''

          success(
            licenseId ? SuccessType.SUCCESS_RENEW : SuccessType.SUCCESS_NEW,
          )

          localStorage.removeItem('form')

          return
        }

        case StateIdModel.EXPIRED: {
          setIsLoading(false)
          setMessage(t('Expired'))
          return
        }

        default: {
          await new Promise(resolve =>
            setTimeout(resolve, PAYMENT_CONFIRM_CHECK_DELAY_MS),
          )
          return getPaymentStatus(paymentId)
        }
      }
    }

    const newLicense = async (
      data: NewLicenseModel,
    ): Promise<PaymentsResponseModel | null> => {
      try {
        return await dispatch(newLicenseThunk(data)).unwrap()
      } catch (errors) {
        setBackendError(errors)
        return null
      }
    }

    const renewalsLicense = async (
      data: PaymentsRequestModel,
    ): Promise<PaymentsResponseModel | null> => {
      try {
        return await dispatch(renewalsLicenseThunk(data)).unwrap()
      } catch (errors) {
        setBackendError(errors)
        return null
      }
    }

    const getData = async (): Promise<PaymentsResponseModel | null> => {
      const s = parse(search)
      const serialNumber = s.serialNumber as string
      const licenseId = s.licenseId as string

      if (licenseId && !serialNumber) {
        return await renewalsLicense({
          discountCode: s.discountCode as string,
          licenseId: s.licenseId as string,
          timePeriodId: s.timePeriodId as string,
        })
      }

      const customerData: CustomerModel = {
        customerEmail: s.customerEmail as string,
        customerName: s.customerName as string,
        customerSurname: s.customerSurname as string,
        customerFarm: s.customerFarm as string,
        countryCode: s.countryCode as string,
        customerAddress: s.customerAddress as string,
        customerPhone: s.customerPhone as string,
        note: s.note as string,
      }

      return await newLicense({
        deviceSerial: serialNumber || '',
        discountCode: s.discountCode as string,
        licenseVersionId: s.licenseVersionId as string,
        timePeriodId: s.timePeriodId as string,
        ...(role === RoleUserModel.Reseller && { ...customerData }),
      })
    }

    const pay = async (e: FormEvent) => {
      e.preventDefault()

      if (!stripe || !elements) {
        return
      }

      try {
        setIsLoading(true)

        const paymentInfo = await getData()

        const cardElement = elements.getElement(CardElement)

        if (!paymentInfo || !cardElement) {
          return
        }

        const { clientSecret, paymentId } = paymentInfo

        const { error } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
          },
        })

        if (error && error.message) {
          setMessage(error.message)
          setIsLoading(false)
          return
        }

        await getPaymentStatus(paymentId)
      } catch (e) {
        setBackendError(e)
        setIsLoading(false)
      }
    }

    const handleChange = useCallback(
      async (e: stripeJs.StripeCardElementChangeEvent) => {
        if (e.error && e.error.message) {
          setMessage(e.error.message)
          setIsLoading(false)
        }
      },
      [],
    )

    return (
      <form className={cnPaymentForm()} onSubmit={pay}>
        <CardElement
          className={cnPaymentForm('form')}
          options={{
            hideIcon: true,
          }}
          onChange={handleChange}
          onReady={onReadyCardElement}
        />
        <div className={cnPaymentForm('btn')}>
          <Button type='submit' isLoading={isLoading} disabled={isLoading}>
            {t('Pay now')}
          </Button>
        </div>

        <Transition in={!!message} timeout={500} mountOnEnter unmountOnExit>
          {state => (
            <div className={cnPaymentForm('error', [state])}>{message}</div>
          )}
        </Transition>
      </form>
    )
  },
)
